import { zodResolver } from '@hookform/resolvers/zod';
import { assert } from 'assert-ts';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { AddIcon, CheckCircle } from '#assets/ReactSvg.ts';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { InlineButton } from '#components/atoms/buttons/inline-button/InlineButton.tsx';
import { ErrorMessage } from '#components/atoms/forms/index.ts';
import { Pill } from '#components/atoms/pill/Pill.tsx';
import type { Network } from '#dn-types/network.ts';
import { AddIpv4RangeModal } from './AddIpv4RangeModal.tsx';
import { useGetStartedMutation } from './DefineNetwork.queries.ts';
import { getStartedFormSchema } from './get-started-form-schema.ts';
import styles from './DefineNetwork.module.css';

// The first step: create the network. IPv6 is assigned automatically and shown
// read-only; an optional IPv4 range is added via a small modal.
export function DefineNetwork({ onCreate }: { onCreate: (network: Network) => void }) {
  const navigate = useNavigate();
  const { handleSubmit, formState, setError, clearErrors, setValue, control } = useForm({
    resolver: zodResolver(getStartedFormSchema),
    mode: 'onChange',
    defaultValues: { cidrV4: '' },
    delayError: 500,
  });
  const getStarted = useGetStartedMutation();
  const { errors, isSubmitting, isSubmitSuccessful } = formState;
  const [modalOpen, setModalOpen] = useState(false);

  const ipv4Cidr = useWatch({ control, name: 'cidrV4' }) ?? '';

  const onSubmit = handleSubmit(async (data) => {
    clearErrors('root.global');
    try {
      const network = await getStarted.mutateAsync({
        name: 'Network1',
        cidrs: data.cidrV4 ? [data.cidrV4] : [],
        description: 'Initial network created with account',
        lighthousesAsRelays: true,
      });
      // addNetwork's reword catch always throws, so a resolved value is defined.
      assert(network, 'expected a network to be created');
      onCreate(network);
    } catch (error) {
      if (error instanceof ValidationError) {
        // A duplicate network name means this account already has its network (the flow hardcodes
        // "Network1"): a revisiting user clicked create during the pre-redirect flash. The form has
        // no name field to fix, so send them to their networks instead of stranding them here.
        if (error.errors.some((d) => d.path === 'name' && d.code === 'ERR_DUPLICATE_VALUE')) {
          void navigate('/settings/networks', { replace: true });
          return;
        }
        handleServerFormError(error, ({ path, message }) => {
          // Only one CIDR is sent, so a cidrs[0] error maps to the single IPv4 range field.
          if (path === 'cidrs[0]') {
            setError('cidrV4', { message });
            return true;
          }
          setError('root.global', { message });
          return true;
        });
      } else {
        trackError(error);
        setError('root.global', { message: 'We were unable to set up your network, please try again.' });
      }
    }
  });

  return (
    <form className={styles.DefineNetwork_defineNetwork} onSubmit={onSubmit}>
      <div>
        <h1 className="h2 mb-8">Define your network</h1>
        <p className={`p-md color-lowlight mb-0 ${styles.DefineNetwork_textMd}`}>
          Every network gets an IPv6 prefix automatically. You can also add an IPv4 range if your workloads need to
          reach IPv4 hosts or use routes.
        </p>
      </div>

      <div>
        <div className={styles.DefineNetwork_addressHeading}>Address space</div>
        <div className={styles.DefineNetwork_addressCard}>
          <div className={`${styles.DefineNetwork_addressRow} ${styles.DefineNetwork_addressRow___center}`}>
            <Pill className={`shrink-0 ${styles.DefineNetwork_ipPill}`}>IPv6</Pill>
            <div className={styles.DefineNetwork_addressMain}>
              {/* No literal example prefix here: the real prefix is only assigned at create time,
                  and anything address-shaped gets copied into firewall rules and allowlists. */}
              <div className={styles.DefineNetwork_addressValue}>Unique IPv6 prefix</div>
            </div>
            <div className={styles.DefineNetwork_addressNote}>
              <CheckCircle width={14} height={14} aria-hidden={true} className="color-success shrink-0" /> Assigned
              automatically
            </div>
          </div>

          {ipv4Cidr ?
            <div className={styles.DefineNetwork_ipv4Section}>
              <div className={`${styles.DefineNetwork_addressRow} ${styles.DefineNetwork_addressRow___center}`}>
                <Pill className={`shrink-0 ${styles.DefineNetwork_ipPill}`}>IPv4</Pill>
                <div className={styles.DefineNetwork_addressMain}>
                  <div className={styles.DefineNetwork_addressValue}>{ipv4Cidr}</div>
                </div>
                <InlineButton
                  className={styles.DefineNetwork_addressRemove}
                  onClick={() => setValue('cidrV4', '', { shouldValidate: true })}
                >
                  Remove
                </InlineButton>
              </div>
            </div>
          : <button type="button" className={styles.DefineNetwork_addIpv4} onClick={() => setModalOpen(true)}>
              <span className={styles.DefineNetwork_addIpv4Plus}>
                <AddIcon width={12} height={12} aria-hidden={true} />
              </span>
              <span className={styles.DefineNetwork_addIpv4Label}>Add an IPv4 range</span>
              <span className={styles.DefineNetwork_addIpv4Optional}>Optional</span>
            </button>
          }
        </div>
        {errors.cidrV4 ?
          <ErrorMessage className="mt-8">{errors.cidrV4.message}</ErrorMessage>
        : null}
      </div>

      {errors.root?.global ?
        <ErrorMessage>{errors.root.global.message}</ErrorMessage>
      : null}

      <div className="flex">
        <Button type="submit" disabled={isSubmitting || isSubmitSuccessful}>
          Create network
        </Button>
      </div>

      {modalOpen ?
        <AddIpv4RangeModal
          defaultCidr={ipv4Cidr}
          onClose={() => setModalOpen(false)}
          onAdd={(cidr) => {
            setValue('cidrV4', cidr, { shouldValidate: true });
            setModalOpen(false);
          }}
        />
      : null}
    </form>
  );
}
